import { Hash, Languages } from "lucide-react";
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatMoney } from "@/lib/currency";

/**
 * LanguageSection — Settings block for choosing UI language + native
 * numerals preference. Persists to localStorage AND server-side
 * (`user.language`) so the choice follows across devices. English
 * is the default.
 *
 * Picking Arabic or Urdu auto-flips the app to RTL unless the user has
 * an explicit direction override in `pfo_direction` (Settings > Layout
 * direction still overrides everything).
 *
 * The numerals toggle appends `-u-nu-arab` (or `-nu-deva` for Hindi)
 * to the locale tag passed to `Intl.toLocaleString`, letting the
 * browser render Eastern-Arabic / Devanagari digits. Default is OFF
 * so screenshots match PayTabs receipts.
 */
export default function LanguageSection({ t }) {
  const { language, setLanguage, languages } = useLanguage();
  const [nativeNumerals, setNativeNumerals] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage?.getItem("pfo_native_numerals") === "1"; }
    catch { return false; }
  });
  const [, forceRender] = useState(0);

  const showNumeralsToggle = ["ar", "ur", "hi"].includes(language);

  const toggleNumerals = () => {
    const next = !nativeNumerals;
    setNativeNumerals(next);
    try { window.localStorage.setItem("pfo_native_numerals", next ? "1" : "0"); }
    catch { /* localStorage disabled — preview only */ }
    // Force a re-render of the preview using the freshly-updated flag.
    forceRender(x => x + 1);
  };

  // If the user switches to a non-numeral-supporting locale, reset the
  // preview state (visually) so the toggle re-appears un-checked next
  // time they visit ar/ur/hi.
  useEffect(() => {
    if (!showNumeralsToggle) forceRender(x => x + 1);
  }, [showNumeralsToggle]);

  return (
    <div className="card-flat p-6" data-testid="settings-language-section">
      <div className="flex items-center gap-2 mb-4">
        <Languages size={14} className="text-[var(--accent-primary)]"/>
        <div className="label-eyebrow">{t("settings.language")}</div>
      </div>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5">
        {t("settings.language_hint")}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {languages.map((l) => {
          const active = language === l.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setLanguage(l.code)}
              data-testid={`settings-lang-${l.code}`}
              aria-pressed={active}
              className="text-left p-3 rounded-xl border transition-all"
              style={{
                borderColor: active ? "var(--accent-primary)" : "var(--border-default)",
                background: active ? "var(--bg-accent)" : "var(--bg-card)",
                boxShadow: active ? "0 0 0 3px rgba(224,158,80,0.18)" : "none",
              }}
            >
              <div className="text-sm font-medium text-[var(--text-primary)]" style={{ direction: l.dir }}>
                {l.native}
              </div>
              <div className="text-[11px] text-[var(--text-muted)] mt-0.5 uppercase tracking-wider">
                {l.label} · {l.code.toUpperCase()}
              </div>
            </button>
          );
        })}
      </div>

      {showNumeralsToggle && (
        <div
          className="mt-6 pt-5 border-t"
          style={{ borderColor: "var(--border-default)" }}
          data-testid="settings-numerals-toggle-section"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <Hash size={14} className="text-[var(--accent-primary)] mt-1 flex-shrink-0"/>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)]">
                  {t("settings.native_numerals")}
                </div>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed mt-1">
                  {t("settings.native_numerals_hint")}
                </p>
                <div className="text-xs font-mono text-[var(--text-muted)] mt-2">
                  {t("settings.native_numerals_preview", { sample: formatMoney(1234.56, "OMR") })}
                </div>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={nativeNumerals}
              onClick={toggleNumerals}
              data-testid="settings-numerals-toggle"
              className="relative flex-shrink-0 h-7 w-12 rounded-full transition-colors"
              style={{
                background: nativeNumerals ? "var(--accent-primary)" : "var(--bg-accent)",
                border: "1px solid var(--border-default)",
              }}
            >
              <span
                className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: nativeNumerals ? "translateX(22px)" : "translateX(2px)" }}
              />
            </button>
          </div>
          <div className="mt-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)]">
            {nativeNumerals ? t("settings.native_numerals_on") : t("settings.native_numerals_off")}
          </div>
        </div>
      )}
    </div>
  );
}
